import { useState, useEffect, useCallback } from 'react';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import { AgencyDashboardService } from '@/services/agencyDashboard.service';
import {
  AgencyAdvisorMemberRow,
  AgencyDashboardPeriod,
} from '@/integrations/supabase/rpc-types';

interface UseAgencyAdvisorMembersResult {
  data: AgencyAdvisorMemberRow[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Il periodo è controllato dal componente padre (condiviso con la tab Panoramica).
 *
 * `enabled` esiste per il filtro Travel Advisor della tab Clienti, che monta anche per
 * travel_designer e travel_advisor_member: la RPC risponde loro con un'eccezione, e senza
 * un modo per non chiamarla ogni advisor si prenderebbe un toast di errore all'apertura
 * della tab.
 */
export function useAgencyAdvisorMembers(
  period: AgencyDashboardPeriod,
  enabled = true
): UseAgencyAdvisorMembersResult {
  const [data, setData] = useState<AgencyAdvisorMemberRow[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const { t } = useI18n();

  const fetchData = useCallback(async () => {
    if (!enabled) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await AgencyDashboardService.getAdvisorMembers(period);
      setData(result);
    } catch {
      const msg = String(t('errors.loadFailed'));
      setError(msg);
      toast({
        title: String(t('common.error')),
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh: fetchData };
}

export default useAgencyAdvisorMembers;
